import { useLocation, useNavigate } from 'react-router-dom';

interface UseTabNavigationOptions<T extends string> {
  validTabs: readonly T[];
  defaultTab: T;
}

/**
 * A hook to manage tab state based on the URL path.
 * It determines the active tab from the last segment of the URL,
 * and provides a handler to navigate to a new tab using relative paths.
 * This makes it independent of the parent routing structure.
 *
 * @param options - Configuration with valid tab names and a default tab.
 * @returns An object with the `activeTab` and a `handleTabChange` function.
 */
export const useTabNavigation = <T extends string>({
  validTabs,
  defaultTab,
}: UseTabNavigationOptions<T>) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get the last part of the path, which we assume is the tab identifier.
  const pathname = location.pathname.endsWith('/')
    ? location.pathname.slice(0, -1)
    : location.pathname;
  const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);

  // Determine the active tab. If the last segment is not a valid tab, use the default.
  // This handles the case for the base path (e.g., /treatment) which should show the default tab.
  const activeTab = validTabs.includes(lastSegment as T)
    ? (lastSegment as T)
    : defaultTab;

  const handleTabChange = (newTab: T) => {
    // Navigate relative to the current route.
    // For the default tab, navigate to '.', which resolves to the current base path.
    // For other tabs, navigate to the sub-path (e.g., 'timeline').
    const path = newTab === defaultTab ? '.' : newTab;
    navigate(path);
  };

  return { activeTab, handleTabChange };
};
